import React, { useState } from "react"
import { Box, Button, Modal, Typography } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import { EVENT_MODAL_TYPE, SNACKBAR_TYPES } from "src/modules/const"
import { Event } from "src/openapi"
import { useMediaQuery } from "react-responsive"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import InputTextField from "src/components/atom/InputTextField"
import dayjs from "dayjs"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"

type Prop = {
  type: number
  item?: Event
  open: boolean
  handleOpen: any
  handlePublic?: any
  handleRemove?: any
  handleArchive?: any
}

export const PublishModal = ({
  type,
  item,
  open,
  handleOpen,
  handlePublic,
  handleRemove,
  handleArchive,
}: Prop) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.event" })

  const isPC = useMediaQuery({ query: "(min-width: 620px)" })

  const [, setShowToast] = useRecoilState(ShowToast)

  const [isPublic, setIsPublic] = useState<boolean>(true)
  const [archiveUrl, setArchiveUrl] = useState<string>("")
  const [publicValidationMessage, setPublicValidationMessage] = useState<string>("")
  const [archiveUrlValidationMessage, setArchiveValidationMessage] = useState<string>("")

  const validateArchive = () => {
    let validation = true

    if (isPublic === undefined) {
      setPublicValidationMessage(t("please_select_archive_type"))
      validation = validation && false
    } else {
      setPublicValidationMessage("")
    }

    if (!archiveUrl) {
      setArchiveValidationMessage(t("please_input_archive_url"))
      validation = validation && false
    } else {
      setArchiveValidationMessage("")
    }

    if (dayjs(item?.finishTime).isAfter(dayjs())) {
      setShowToast({ show: true, text: t("not_over_finish_time"), type: SNACKBAR_TYPES.WARNING })
      validation = validation && false
    }

    return validation
  }

  const process = () => {
    if (type == EVENT_MODAL_TYPE.PUBLIC) {
      handlePublic()
    } else if (type == EVENT_MODAL_TYPE.REMOVE) {
      handleRemove()
    } else if (type === EVENT_MODAL_TYPE.ARCHIVE) {
      if (!validateArchive()) {
        return
      }

      handleArchive(isPublic, archiveUrl)
    }
    handleOpen(false)
  }

  return (
    <Modal
      open={open}
      onClose={() => handleOpen(false)}
      sx={{ height: "auto" }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      containerProps={{
        width:
          type === EVENT_MODAL_TYPE.ARCHIVE && isPC
            ? "600px"
            : type === EVENT_MODAL_TYPE.ARCHIVE && !isPC
            ? "98%"
            : "340px",
      }}
    >
      <Box
        width="sm"
        display="flex"
        flexDirection="column"
        sx={{ borderRadius: "10px", bgcolor: "white", borderColor: "white", p: "0px" }}
      >
        <Box sx={{ p: "0px 10px" }}>
          {type === EVENT_MODAL_TYPE.REMOVE && (
            <>
              <Typography.CardHead
                sx={{ fontSize: "16px", fontFamily: "Noto Sans JP !important;", fontWeight: "700" }}
              >
                {t("do_you_want_to_remove")}
              </Typography.CardHead>
              <Typography.CardHead
                sx={{
                  color: "#FC3317",
                  fontSize: "14px",
                  fontFamily: "Noto Sans JP !important;",
                  fontWeight: "700",
                  mt: "40px",
                }}
              >
                {t("remove_warning")}
              </Typography.CardHead>
              <Box display="block" textAlign="center">
                <Button
                  variant="outlined"
                  className="notosans"
                  sx={{
                    my: "5px",
                    mx: "0.5rem",
                    width: 240,
                    height: 48,
                    fontSize: 16,
                    borderRadius: 0,
                    borderColor: "#FC3317",
                    color: "#FC3317",
                  }}
                  onClick={() => process()}
                >
                  {t("remove")}
                </Button>
                <Button
                  variant="outlined"
                  className="notosans"
                  sx={{
                    my: "5px",
                    mx: "0.5rem",
                    width: 240,
                    height: 48,
                    fontSize: 16,
                    borderRadius: 0,
                    borderColor: "#1F6757",
                    color: "#1F6757",
                  }}
                  onClick={() => handleOpen(false)}
                >
                  {t("back")}
                </Button>
              </Box>
            </>
          )}
          {type === EVENT_MODAL_TYPE.PUBLIC && (
            <>
              <Typography.CardHead
                sx={{ fontSize: "16px", fontFamily: "Noto Sans JP !important;", fontWeight: "700" }}
              >
                {item?.isPublic ? t("do_you_want_to_private") : t("do_you_want_to_public")}
              </Typography.CardHead>
              <Box display="block" sx={{ mt: "40px" }} textAlign="center">
                <Button
                  variant="outlined"
                  className="notosans"
                  sx={{
                    my: "5px",
                    mx: "0.5rem",
                    width: 240,
                    height: 48,
                    fontSize: 16,
                    borderRadius: 0,
                    borderColor: "#FFB800",
                    color: "#FFB800",
                  }}
                  onClick={() => process()}
                >
                  {item?.isPublic ? t("private") : t("public")}
                </Button>
                <Button
                  variant="outlined"
                  className="notosans"
                  sx={{
                    my: "5px",
                    mx: "0.5rem",
                    width: 240,
                    height: 48,
                    fontSize: 16,
                    borderRadius: 0,
                    borderColor: "#1F6757",
                    color: "#1F6757",
                  }}
                  onClick={() => handleOpen(false)}
                >
                  {t("back")}
                </Button>
              </Box>
            </>
          )}
          {type === EVENT_MODAL_TYPE.ARCHIVE && (
            <>
              <Typography.CardHead
                sx={{ fontSize: "16px", fontFamily: "Noto Sans JP !important;", fontWeight: "700" }}
              >
                {t("do_you_want_to_archive")}
              </Typography.CardHead>
              <Box sx={{ mt: "40px" }}>
                <CheckBoxGroupField
                  required
                  radio
                  name={t("archive_type")}
                  selected={isPublic}
                  handleSelected={setIsPublic}
                  datas={[
                    {
                      label: t("archive_type_public"),
                      value: true,
                    },
                    {
                      label: t("archive_type_private"),
                      value: false,
                    },
                  ]}
                  validationMessage={publicValidationMessage}
                />
                <InputTextField
                  required
                  name={t("archive_url")}
                  text={archiveUrl}
                  handleText={setArchiveUrl}
                  validationMessage={archiveUrlValidationMessage}
                  inputStyle={{ width: "100%" }}
                  sx={{ mt: "40px" }}
                />
              </Box>
              <Box display="block" sx={{ mt: "10px" }} textAlign="center">
                <Button
                  variant="outlined"
                  className="notosans"
                  sx={{
                    my: "5px",
                    mx: "0.5rem",
                    width: 240,
                    height: 48,
                    fontSize: 16,
                    borderRadius: 0,
                    borderColor: "#235496",
                    color: "#235496",
                  }}
                  onClick={() => process()}
                >
                  {t("archive")}
                </Button>
                <Button
                  variant="outlined"
                  className="notosans"
                  sx={{
                    my: "5px",
                    mx: "0.5rem",
                    width: 240,
                    height: 48,
                    fontSize: 16,
                    borderRadius: 0,
                    borderColor: "#1F6757",
                    color: "#1F6757",
                  }}
                  onClick={() => handleOpen(false)}
                >
                  {t("back")}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  )
}
